import { queueWorkspaceNativeMutation } from './workspaceMutationQueue'
import { assertWorkspaceActive } from '../data/workspace'
import {
  LocalNotifications,
  type LocalNotificationSchema,
  type LocalNotificationsPlugin,
} from '@capacitor/local-notifications'
import { APP_SETTINGS } from '../config/appSettings'
import { acknowledgeReminder, type Reminder } from '../domain/reminder'
import type { Note } from '../domain/note'
import { getNativePlatform } from '../utils/nativeRuntime'

const REMINDER_CHANNEL_ID = 'reminders'
const REMINDER_NOTIFICATION_SOURCE = 'minimany-reminder'
const REMINDER_ACTION_TYPE_ID = 'minimany-reminder-actions'
const REMINDER_ACTION_OPEN = 'minimany-open'
const REMINDER_ACTION_ACKNOWLEDGE = 'minimany-got-it'
const REMINDER_ACTION_SNOOZE = 'minimany-snooze'

interface ReminderNotificationExtra {
  source: typeof REMINDER_NOTIFICATION_SOURCE
  noteId: string
  occurrenceAt?: number
}

export type NativeReminderAction = 'open' | 'acknowledge' | 'snooze'

export interface NativeReminderNotificationAction {
  action: NativeReminderAction
  noteId: string
  occurrenceAt: number | null
}

interface NativeReminderNotificationActionHandlers {
  openNote: (noteId: string) => void
  queueAction: (action: NativeReminderNotificationAction & {
    action: Exclude<NativeReminderAction, 'open'>
  }) => void
}

export interface NativeReminderScheduleInput {
  noteId: string
  noteTitle: string
  reminder: Reminder
}

/**
 * What Android is asked to remind about: each note's reminder, except a note in Trash. Its reminder stays on the note, so restoring the
 * note brings it back, but a deleted note never notifies, wherever it was deleted.
 */
export function reminderScheduleInputs(notes: Iterable<Note>): NativeReminderScheduleInput[] {
  return [...notes].flatMap((note) => note.reminder && note.status !== 'trash'
    ? [{ noteId: note.id, noteTitle: note.manualTitle ?? note.derivedTitle, reminder: note.reminder }]
    : [])
}

export interface NativeReminderScheduleResult {
  status: 'scheduled' | 'permission-denied' | 'unavailable'
  exact: boolean
}

export type NativeReminderNotificationsPlugin = Pick<
  LocalNotificationsPlugin,
  'addListener' | 'cancel' | 'checkExactNotificationSetting' | 'checkPermissions' | 'createChannel' | 'getPending' | 'registerActionTypes' | 'requestPermissions' | 'schedule'
> & Partial<Pick<LocalNotificationsPlugin, 'getDeliveredNotifications' | 'removeDeliveredNotifications'>>

function reminderNotificationId(noteId: string, occurrence: number): number {
  let hash = 2_166_136_261
  const input = `${noteId}:${occurrence}`
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index)
    hash = Math.imul(hash, 16_777_619)
  }
  return (hash >>> 0) & 0x7fff_ffff || 1
}

function isReminderExtra(value: unknown): value is ReminderNotificationExtra {
  if (!value || typeof value !== 'object') return false
  const extra = value as Partial<ReminderNotificationExtra>
  return extra.source === REMINDER_NOTIFICATION_SOURCE && typeof extra.noteId === 'string'
}

function upcomingOccurrences(reminder: Reminder, now: number): Reminder[] {
  const occurrences: Reminder[] = []
  let current = reminder
  let skipped = 0
  const skipLimit = 1_024
  while (current.nextAt <= now && current.recurrence && skipped < skipLimit) {
    const advanced = acknowledgeReminder(current, now)
    if (!advanced) break
    current = advanced
    skipped += 1
  }
  while (occurrences.length < APP_SETTINGS.nativeNotifications.recurrenceHorizonOccurrences) {
    if (current.nextAt > now) occurrences.push(current)
    const advanced = acknowledgeReminder(current, now)
    if (!advanced) break
    current = advanced
  }
  return occurrences
}

function notificationFor(
  { noteId, noteTitle }: NativeReminderScheduleInput,
  occurrence: Reminder,
  index: number,
  exact: boolean,
): LocalNotificationSchema {
  const title = occurrence.title || noteTitle || 'miniMany reminder'
  return {
    id: reminderNotificationId(noteId, index),
    title,
    body: 'Reminder from miniMany',
    largeBody: title,
    channelId: REMINDER_CHANNEL_ID,
    autoCancel: true,
    isExactNotification: exact,
    isExactMandatory: false,
    actionTypeId: REMINDER_ACTION_TYPE_ID,
    extra: {
      source: REMINDER_NOTIFICATION_SOURCE,
      noteId,
      occurrenceAt: occurrence.nextAt,
    } satisfies ReminderNotificationExtra,
    schedule: {
      at: new Date(occurrence.nextAt),
      allowWhileIdle: true,
    },
  }
}

export function getNativeReminderNotifications(
  container: unknown = globalThis,
  plugin: NativeReminderNotificationsPlugin = LocalNotifications,
): NativeReminderNotificationsPlugin | null {
  return getNativePlatform(container) === 'android' ? plugin : null
}

export function noteIdFromNativeReminderNotification(value: unknown): string | null {
  return nativeReminderNotificationAction(value)?.noteId ?? null
}

export function nativeReminderNotificationAction(value: unknown): NativeReminderNotificationAction | null {
  if (!value || typeof value !== 'object') return null
  const notification = value as { actionId?: unknown, notification?: { extra?: unknown } }
  const extra = notification.notification?.extra
  if (!isReminderExtra(extra)) return null
  const action = notification.actionId === REMINDER_ACTION_ACKNOWLEDGE
    ? 'acknowledge'
    : notification.actionId === REMINDER_ACTION_SNOOZE
      ? 'snooze'
      : 'open'
  return {
    action,
    noteId: extra.noteId,
    occurrenceAt: typeof extra.occurrenceAt === 'number' && Number.isFinite(extra.occurrenceAt)
      ? extra.occurrenceAt
      : null,
  }
}

export function dispatchNativeReminderNotificationAction(
  value: unknown,
  handlers: NativeReminderNotificationActionHandlers,
): boolean {
  const notificationAction = nativeReminderNotificationAction(value)
  if (!notificationAction) return false

  // Got it is an acknowledgement-only action. Snooze still needs the note-backed
  // choice sheet, while Open and a body tap intentionally navigate to the note.
  if (notificationAction.action !== 'acknowledge') {
    handlers.openNote(notificationAction.noteId)
  }
  if (notificationAction.action !== 'open') {
    handlers.queueAction({ ...notificationAction, action: notificationAction.action })
  }
  return true
}

async function cancelMiniManyNotifications(plugin: NativeReminderNotificationsPlugin): Promise<void> {
  const pending = await plugin.getPending()
  const notifications = pending.notifications
    .filter((notification) => isReminderExtra(notification.extra))
    .map(({ id }) => ({ id }))
  if (notifications.length > 0) await plugin.cancel({ notifications })
}

async function configureChannel(plugin: NativeReminderNotificationsPlugin): Promise<void> {
  await plugin.createChannel({
    id: REMINDER_CHANNEL_ID,
    name: 'Reminders',
    description: 'miniMany note reminders',
    importance: 4,
    visibility: 1,
  })
  await plugin.registerActionTypes({
    types: [{
      id: REMINDER_ACTION_TYPE_ID,
      actions: [
        { id: REMINDER_ACTION_SNOOZE, title: 'Snooze' },
        { id: REMINDER_ACTION_OPEN, title: 'Open' },
      ],
    }],
  })
}

async function canDisplayNotifications(
  plugin: NativeReminderNotificationsPlugin,
  requestPermission: boolean,
): Promise<boolean> {
  const current = await plugin.checkPermissions()
  if (current.display === 'granted') return true
  if (!requestPermission) return false
  return (await plugin.requestPermissions()).display === 'granted'
}

export async function requestNativeReminderPermission(
  plugin: NativeReminderNotificationsPlugin | null = getNativeReminderNotifications(),
): Promise<'granted' | 'denied' | 'unavailable'> {
  if (!plugin) return 'unavailable'
  return await canDisplayNotifications(plugin, true) ? 'granted' : 'denied'
}

/**
 * Replaces only miniMany-owned pending notifications. Background reconciliation never
 * presents an OS permission or exact-alarm settings screen; explicit reminder saves do.
 */
async function reconcileNativeRemindersNow(
  reminders: NativeReminderScheduleInput[],
  options: {
    requestPermission?: boolean
    requestExactAlarm?: boolean
    plugin?: NativeReminderNotificationsPlugin
  } = {},
): Promise<NativeReminderScheduleResult> {
  const plugin = options.plugin ?? getNativeReminderNotifications()
  if (!plugin) return { status: 'unavailable', exact: false }
  if (!await canDisplayNotifications(plugin, options.requestPermission === true)) {
    return { status: 'permission-denied', exact: false }
  }

  await configureChannel(plugin)
  const exactPermission = await plugin.checkExactNotificationSetting()
  const exact = options.requestExactAlarm === true || exactPermission.exact_alarm === 'granted'
  await cancelMiniManyNotifications(plugin)
  const notifications = reminders.flatMap((input) => upcomingOccurrences(input.reminder, Date.now())
    .map((occurrence, index) => notificationFor(input, occurrence, index, exact)))
  assertWorkspaceActive()
  if (notifications.length > 0) await plugin.schedule({ notifications })
  return { status: 'scheduled', exact }
}

export function clearNativeReminderNotifications(plugin: NativeReminderNotificationsPlugin): Promise<void> {
  return queueWorkspaceNativeMutation(async () => {
    await cancelMiniManyNotifications(plugin)
    if (plugin.getDeliveredNotifications && plugin.removeDeliveredNotifications) {
      const delivered = await plugin.getDeliveredNotifications()
      const notifications = delivered.notifications.filter(notification => isReminderExtra(notification.extra))
      if (notifications.length) await plugin.removeDeliveredNotifications({ notifications })
    }
  }, true)
}
export function reconcileNativeReminderNotifications(
  reminders: NativeReminderScheduleInput[],
  options: { requestPermission?: boolean; requestExactAlarm?: boolean; plugin?: NativeReminderNotificationsPlugin } = {},
): Promise<NativeReminderScheduleResult> {
  return queueWorkspaceNativeMutation(() => reconcileNativeRemindersNow(reminders, options))
}
